if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      get cart() {
        // Always get fresh reference to handle multiple forms on page
        return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Check if this is a gift card and the "Send this as a gift" checkbox is checked
        const giftCheckbox = this.form.querySelector('.product-form__gift-checkbox');
        if (giftCheckbox && giftCheckbox.checked) {
          // Get product data for gift card handling
          const productHandle = this.dataset.productHandle;
          if (productHandle) {
            this.handleGiftCardFlow(productHandle, evt);
            return;
          }
        }

        // Delivery date step: for non-exempt products, resolve the delivery date and
        // sync it onto the cart attribute BEFORE adding. requireSelection() returns the
        // already-chosen date immediately, or opens the calendar modal on first add.
        // We always (re)write the attribute here: needsSelection() reads localStorage
        // while the cart/checkout validation reads the `delivery_date` cart attribute,
        // and the two can drift (e.g. a fresh or abandoned cart that still has a stored
        // date). A dateless add is rejected by the validation function, so syncing the
        // attribute first is what lets the add succeed. Exempt products skip this.
        if (!this.isDeliveryExempt() && window.StockedDelivery) {
          const fmt = window.StockedDeliveryFormat;
          const specialMax = this.specialMaxDate();
          // Effective ceiling = min(this Special's threshold, tightest Special already in cart).
          const effectiveMax = specialMax
            ? (fmt && fmt.minISO ? fmt.minISO(specialMax, this.cartSpecialMaxDate()) : specialMax)
            : null;

          const run = () => {
            // needsSelection() is true only when no date is stored yet, i.e. the modal
            // is about to be shown for this add. Capture it before requireSelection().
            const dateModalWasShown =
              typeof window.StockedDelivery.needsSelection === 'function'
                ? window.StockedDelivery.needsSelection()
                : false;
            window.StockedDelivery.requireSelection(effectiveMax ? { maxDate: effectiveMax } : {})
              .then(async (selection) => {
                if (!selection) return; // dismissed / no valid dates → do not add
                this.startLoading();
                await window.StockedDelivery.setCartAttributes(selection);
                this.proceedWithSubmission(evt, { dateModalWasShown });
              })
              .catch((error) => {
                console.error('Delivery date selection error:', error);
                this.proceedWithSubmission(evt, { dateModalWasShown });
              });
          };

          // Special with an already-chosen date that's too late: confirm first.
          if (effectiveMax) {
            const sel = window.StockedDelivery.getSelection();
            if (sel && sel.iso > effectiveMax) {
              window.StockedDelivery.promptSpecialConflict().then((choice) => {
                if (choice === 'change') run(); // opens the constrained calendar
                // 'cancel' → abort the add (button stays idle)
              });
              return;
            }
          }

          run();
          return;
        }

        this.proceedWithSubmission(evt);
      }

      isDeliveryExempt() {
        if (this._deliveryExempt !== undefined) return this._deliveryExempt;
        let exempt = false;
        const productHandle = this.dataset.productHandle;
        if (productHandle) {
          try {
            const productDataElement = document.getElementById(`product-data-${productHandle}`);
            if (productDataElement) {
              const productData = JSON.parse(productDataElement.textContent);
              exempt = productData?.product?.weekly_menu_exempt === 1;
            }
          } catch (error) {
            console.error('Error checking product exempt status:', error);
          }
        }
        this._deliveryExempt = exempt;
        return exempt;
      }

      // The Special's own delivery threshold (ISO) from product-data JSON, or null.
      specialMaxDate() {
        if (this._specialMax !== undefined) return this._specialMax;
        let iso = null;
        const productHandle = this.dataset.productHandle;
        if (productHandle) {
          try {
            const el = document.getElementById(`product-data-${productHandle}`);
            if (el) {
              const data = JSON.parse(el.textContent);
              const raw = data?.product?.deliver_up_to_date;
              if (typeof raw === 'string' && raw) iso = raw.slice(0, 10);
            }
          } catch (error) {
            console.error('Error reading special threshold:', error);
          }
        }
        this._specialMax = iso;
        return iso;
      }

      // The tightest Special threshold already in the cart (ISO), from the inline
      // cart calendar's data-max-date, or null.
      cartSpecialMaxDate() {
        const el = document.querySelector('cart-delivery-date:not([data-mode="modal"])');
        return (el && el.dataset && el.dataset.maxDate) || null;
      }

      proceedWithSubmission(evt, options = {}) {
        this.handleErrorMessage();

        // Capture before the add: the drawer's is-empty class reflects the pre-add
        // cart, and the finally-block below strips it after a successful add.
        const cartWasEmpty = !!(this.cart && this.cart.classList.contains('is-empty'));
        const dateModalWasShown = !!options.dateModalWasShown;

        this.startLoading();

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              location.reload();
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error) {
              const cartResponse = await fetch('/cart.js');
              const fullCartData = await cartResponse.json();

              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: fullCartData,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            }

            this.error = false;
            const shouldOpen = window.CartOpenPolicy
              ? window.CartOpenPolicy.shouldOpenCart({ cartWasEmpty, dateModalWasShown })
              : true;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response, { open: shouldOpen });
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response, { open: shouldOpen });
              });
              if (!shouldOpen) this.showAddedState();
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      showAddedState() {
        if (this._defaultButtonText === undefined) {
          this._defaultButtonText = this.submitButtonText.textContent;
        }
        this.submitButton.classList.add('added');
        this.submitButtonText.textContent = window.variantStrings.added;
        clearTimeout(this._addedTimer);
        this._addedTimer = setTimeout(() => this.resetAddedState(), 2000);
      }

      resetAddedState() {
        this.submitButton.classList.remove('added');
        if (this._defaultButtonText !== undefined) {
          this.submitButtonText.textContent = this._defaultButtonText;
        }
      }

      startLoading() {
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');
      }

      handleGiftCardFlow(productHandle, evt) {
        // Get the gift card selector component
        const giftCardSelector = document.querySelector('gift-card-selector');
        if (!giftCardSelector) {
          console.error('Gift card selector not found');
          // Fallback to normal submission
          this.proceedWithSubmission(evt);
          return;
        }

        // Get product data
        const productDataElement = document.getElementById(`product-data-${productHandle}`);
        if (!productDataElement) {
          console.error('Product data not found for handle:', productHandle);
          this.proceedWithSubmission(evt);
          return;
        }

        try {
          const productData = JSON.parse(productDataElement.textContent);

          // Get the selected variant
          let selectedVariant = null;
          const selectedVariantId = parseInt(this.variantIdInput.value);
          
          // Check if variants exist in the product data structure
          if (productData.product && productData.product.variants) {
            selectedVariant = productData.product.variants.find(v => v.id === selectedVariantId);
          } else if (productData.variants) {
            selectedVariant = productData.variants.find(v => v.id === selectedVariantId);
          }

          // If variant not found in data, create a basic variant object from form
          if (!selectedVariant) {
            // Try to get variant data from the page's variant data
            const variantScripts = document.querySelectorAll('[data-variant-id]');
            for (const script of variantScripts) {
              if (script.dataset.variantId == selectedVariantId) {
                try {
                  const variantData = JSON.parse(script.textContent);
                  selectedVariant = variantData;
                  break;
                } catch (e) {
                  // Continue to next script
                }
              }
            }
            
            // Final fallback - create basic variant object
            if (!selectedVariant) {
              selectedVariant = {
                id: selectedVariantId,
                title: document.querySelector('.product__title')?.textContent || 'Gift Card',
                price: document.querySelector('[data-price]')?.dataset.price || 0
              };
            }
          }


          // Collect form data
          const formData = new FormData(this.form);
          
          // Show the gift card selector
          giftCardSelector.showGiftCardForm(productData.product || productData, selectedVariant, formData);
          
        } catch (error) {
          console.error('Error parsing product data:', error);
          this.proceedWithSubmission(evt);
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
